import { useState, useEffect } from 'react';
import { motion, Reorder } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronRight, Plus, Edit2, Trash2, Loader2, Gift,
  GripVertical, ToggleLeft, ToggleRight, ArrowLeft, Save, Settings, Sparkles, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { generateGiftVoucherPDF, formatDateFr } from '../lib/giftVoucherPdf';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGiftCardsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('suivi');
  
  // Preset amounts state
  const [presetAmounts, setPresetAmounts] = useState([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(true);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  
  // Configuration state
  const [config, setConfig] = useState({
    allow_custom_amount: true,
    min_custom_amount: 10,
    max_custom_amount: 500
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  
  // Prestations state
  const [prestations, setPrestations] = useState([]);
  const [isLoadingPrestations, setIsLoadingPrestations] = useState(true);

  // Gift voucher tracking state
  const [vouchers, setVouchers] = useState([]);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(true);
  const [onlyActiveVouchers, setOnlyActiveVouchers] = useState(true);
  const [isGeneratingVoucher, setIsGeneratingVoucher] = useState(false);
  const [voucherForm, setVoucherForm] = useState(() => ({
    client_name: '',
    sender_name: '',
    amount: '',
    message: '',
    purchase_date: new Date().toISOString().split('T')[0],
    voucher_number: ''
  }));
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [formData, setFormData] = useState({ amount: '', description: '', is_active: true });
  const [isSaving, setIsSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPresetAmounts();
    fetchConfig();
    fetchPrestations();
    fetchVouchers();
    fetchNewVoucherNumber();
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [onlyActiveVouchers]);

  // ============================================
  // PRESET AMOUNTS
  // ============================================

  const fetchPresetAmounts = async () => {
    setIsLoadingPresets(true);
    try {
      const response = await axios.get(`${API_URL}/gift-card/admin/preset-amounts?include_inactive=true`, { headers });
      setPresetAmounts(response.data || []);
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error fetching preset amounts:', err);
      toast.error('Erreur lors du chargement des montants');
    } finally {
      setIsLoadingPresets(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPreset(null);
    setFormData({ amount: '', description: '', is_active: true });
    setIsDialogOpen(true);
  };

  const openEditDialog = (preset) => {
    setEditingPreset(preset);
    setFormData({
      amount: preset.amount.toString(),
      description: preset.description || '',
      is_active: preset.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSavePreset = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        is_active: formData.is_active
      };

      if (editingPreset) {
        await axios.put(`${API_URL}/gift-card/admin/preset-amounts/${editingPreset.id}`, payload, { headers });
        toast.success('Montant modifié');
      } else {
        await axios.post(`${API_URL}/gift-card/admin/preset-amounts`, payload, { headers });
        toast.success('Montant créé');
      }
      setIsDialogOpen(false);
      fetchPresetAmounts();
    } catch (err) {
      console.error('Error saving preset:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePreset = async (preset) => {
    if (!window.confirm(`Supprimer le montant "${preset.amount}€" ?`)) return;
    
    try {
      await axios.delete(`${API_URL}/gift-card/admin/preset-amounts/${preset.id}`, { headers });
      toast.success('Montant supprimé');
      fetchPresetAmounts();
    } catch (err) {
      console.error('Error deleting preset:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePreset = async (preset) => {
    try {
      await axios.patch(`${API_URL}/gift-card/admin/preset-amounts/${preset.id}/toggle`, {}, { headers });
      toast.success(preset.is_active ? 'Montant désactivé' : 'Montant activé');
      fetchPresetAmounts();
    } catch (err) {
      console.error('Error toggling preset:', err);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleReorderPresets = (newOrder) => {
    setPresetAmounts(newOrder);
    setHasOrderChanges(true);
  };

  const savePresetsOrder = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/gift-card/admin/preset-amounts/reorder`, {
        preset_ids: presetAmounts.map(p => p.id)
      }, { headers });
      toast.success('Ordre enregistré');
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // CONFIGURATION
  // ============================================

  const fetchConfig = async () => {
    setIsLoadingConfig(true);
    try {
      const response = await axios.get(`${API_URL}/gift-card/admin/config`, { headers });
      setConfig(response.data);
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/gift-card/admin/config`, config, { headers });
      toast.success('Configuration enregistrée');
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // PRESTATIONS ELIGIBILITY
  // ============================================

  const fetchPrestations = async () => {
    setIsLoadingPrestations(true);
    try {
      const response = await axios.get(`${API_URL}/gift-card/admin/prestations`, { headers });
      setPrestations(response.data.prestations || []);
    } catch (err) {
      console.error('Error fetching prestations:', err);
    } finally {
      setIsLoadingPrestations(false);
    }
  };

  const handleTogglePrestationEligible = async (prestation) => {
    try {
      await axios.patch(`${API_URL}/gift-card/admin/prestations/${prestation.id}/gift-eligible`, {
        gift_eligible: !prestation.gift_eligible
      }, { headers });
      
      setPrestations(prev => prev.map(p => 
        p.id === prestation.id ? { ...p, gift_eligible: !p.gift_eligible } : p
      ));
      
      toast.success(prestation.gift_eligible ? 'Prestation retirée' : 'Prestation ajoutée');
    } catch (err) {
      console.error('Error toggling prestation:', err);
      toast.error('Erreur lors de la modification');
    }
  };

  // ============================================
  // GIFT VOUCHER TRACKING
  // ============================================

  const voucherStatusLabels = {
    en_cours: 'En cours',
    utilise: 'Utilisé',
    depasse: 'Dépassé'
  };

  const fetchVouchers = async () => {
    setIsLoadingVouchers(true);
    try {
      const response = await axios.get(
        `${API_URL}/gift-card/admin/vouchers?only_active=${onlyActiveVouchers}`,
        { headers }
      );
      setVouchers(response.data || []);
    } catch (err) {
      console.error('Error fetching vouchers:', err);
      toast.error('Erreur lors du chargement des bons cadeaux');
    } finally {
      setIsLoadingVouchers(false);
    }
  };

  const fetchNewVoucherNumber = async () => {
    try {
      const response = await axios.get(`${API_URL}/gift-card/admin/vouchers/new-number`, { headers });
      setVoucherForm(prev => ({ ...prev, voucher_number: response.data?.voucher_number || prev.voucher_number }));
    } catch (err) {
      console.error('Error generating voucher number:', err);
      toast.error('Impossible de générer un numéro de bon');
    }
  };

  const resetVoucherForm = () => {
    setVoucherForm({
      client_name: '',
      sender_name: '',
      amount: '',
      message: '',
      purchase_date: new Date().toISOString().split('T')[0],
      voucher_number: ''
    });
    fetchNewVoucherNumber();
  };

  const handleCreateVoucher = async () => {
    if (!voucherForm.client_name.trim()) {
      toast.error('Le bénéficiaire est requis');
      return;
    }
    if (!voucherForm.sender_name.trim()) {
      toast.error('Le champ \"de la part de\" est requis');
      return;
    }
    if (!voucherForm.amount || parseFloat(voucherForm.amount) <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }
    if (!voucherForm.purchase_date) {
      toast.error("La date d'achat est requise");
      return;
    }

    setIsGeneratingVoucher(true);
    try {
      const payload = {
        client_name: voucherForm.client_name.trim(),
        sender_name: voucherForm.sender_name.trim(),
        amount: parseFloat(voucherForm.amount),
        message: voucherForm.message?.trim() || null,
        purchase_date: voucherForm.purchase_date,
        voucher_number: voucherForm.voucher_number || null
      };

      const response = await axios.post(`${API_URL}/gift-card/admin/vouchers`, payload, { headers });
      const created = response.data;

      await generateGiftVoucherPDF({
        clientName: created.client_name,
        senderName: created.sender_name,
        amount: created.amount,
        message: created.message || '',
        purchaseDate: created.purchase_date,
        validUntil: created.valid_until,
        voucherNumber: created.voucher_number,
        download: true
      });

      toast.success('Bon cadeau généré et téléchargé');
      fetchVouchers();
      resetVoucherForm();
    } catch (err) {
      console.error('Error creating voucher:', err);
      toast.error(err?.response?.data?.detail || 'Erreur lors de la génération du bon cadeau');
    } finally {
      setIsGeneratingVoucher(false);
    }
  };

  const handleChangeVoucherStatus = async (voucherId, status) => {
    try {
      await axios.patch(`${API_URL}/gift-card/admin/vouchers/${voucherId}/status`, { status }, { headers });
      setVouchers(prev => prev.map(v => (v.id === voucherId ? { ...v, status } : v)));
      if (onlyActiveVouchers && status !== 'en_cours') {
        fetchVouchers();
      }
      toast.success('Statut mis à jour');
    } catch (err) {
      console.error('Error updating voucher status:', err);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteVoucher = async (voucher) => {
    if (!window.confirm(`Supprimer le bon cadeau \"${voucher.voucher_number}\" ?`)) return;
    try {
      await axios.delete(`${API_URL}/gift-card/admin/vouchers/${voucher.id}`, { headers });
      toast.success('Bon cadeau supprimé');
      fetchVouchers();
    } catch (err) {
      console.error('Error deleting voucher:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadVoucher = async (voucher) => {
    try {
      await generateGiftVoucherPDF({
        clientName: voucher.client_name,
        senderName: voucher.sender_name,
        amount: voucher.amount,
        message: voucher.message || '',
        purchaseDate: voucher.purchase_date,
        validUntil: voucher.valid_until,
        voucherNumber: voucher.voucher_number,
        download: true
      });
    } catch (err) {
      console.error('Error downloading voucher:', err);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  // Group prestations by category
  const groupedPrestations = prestations.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const categoryLabels = {
    onglerie: 'Onglerie',
    pedicure: 'Pédicure',
    bien_etre: 'Bien-être'
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-12 px-6 bg-[var(--charcoal)]">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Cartes Cadeaux
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Gérez les options de cartes cadeaux
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Cartes Cadeaux</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1000px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              <ArrowLeft size={16} />
              Retour
            </Button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="suivi" className="gap-2">
                <Gift size={16} />
                Suivi Bons
              </TabsTrigger>
              <TabsTrigger value="montants" className="gap-2">
                <Gift size={16} />
                Montants
              </TabsTrigger>
              <TabsTrigger value="config" className="gap-2">
                <Settings size={16} />
                Configuration
              </TabsTrigger>
              <TabsTrigger value="prestations" className="gap-2">
                <Sparkles size={16} />
                Prestations
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Voucher Tracking */}
            <TabsContent value="suivi">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-[var(--champagne)] p-6">
                  <h2 className="font-['Cormorant_Garamond'] text-2xl mb-2">Générer un bon cadeau</h2>
                  <p className="text-sm text-[var(--charcoal)]/60 mb-6">
                    Données et logique alignées sur le fichier de référence (validité 6 mois).
                  </p>

                  <div className="space-y-4">
                    <div>
                      <Label>Bénéficiaire (Client concerné) <span className="text-red-500">*</span></Label>
                      <Input
                        value={voucherForm.client_name}
                        onChange={(e) => setVoucherForm(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="ex : Sophie Martin"
                      />
                    </div>

                    <div>
                      <Label>De la part de <span className="text-red-500">*</span></Label>
                      <Input
                        value={voucherForm.sender_name}
                        onChange={(e) => setVoucherForm(prev => ({ ...prev, sender_name: e.target.value }))}
                        placeholder="ex : Jean Martin"
                      />
                    </div>

                    <div>
                      <Label>Montant (€) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        min={1}
                        value={voucherForm.amount}
                        onChange={(e) => setVoucherForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="50"
                      />
                    </div>

                    <div>
                      <Label>Message personnalisé</Label>
                      <Textarea
                        value={voucherForm.message}
                        onChange={(e) => setVoucherForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Un message tendre..."
                        className="min-h-[80px]"
                      />
                    </div>

                    <div>
                      <Label>Date d'achat <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={voucherForm.purchase_date}
                        onChange={(e) => setVoucherForm(prev => ({ ...prev, purchase_date: e.target.value }))}
                      />
                    </div>

                    <div>
                      <Label>N° du bon cadeau</Label>
                      <div className="flex gap-2">
                        <Input
                          value={voucherForm.voucher_number}
                          onChange={(e) => setVoucherForm(prev => ({ ...prev, voucher_number: e.target.value.toUpperCase() }))}
                          placeholder="MD-YYYYMM-XXXXX"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={fetchNewVoucherNumber}
                          className="rounded-none"
                        >
                          Régénérer
                        </Button>
                      </div>
                    </div>

                    <Button
                      onClick={handleCreateVoucher}
                      disabled={isGeneratingVoucher}
                      className="w-full bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
                    >
                      {isGeneratingVoucher ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      Générer et télécharger PDF
                    </Button>
                  </div>
                </div>

                <div className="bg-white border border-[var(--champagne)] p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h2 className="font-['Cormorant_Garamond'] text-2xl">Suivi des bons cadeaux</h2>
                      <p className="text-sm text-[var(--charcoal)]/60">
                        Statuts: En cours, Utilisé, Dépassé
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[var(--charcoal)]/70 cursor-pointer">
                      <Checkbox
                        checked={onlyActiveVouchers}
                        onCheckedChange={(checked) => setOnlyActiveVouchers(Boolean(checked))}
                      />
                      N'afficher que les en cours
                    </label>
                  </div>

                  {isLoadingVouchers ? (
                    <div className="flex justify-center py-10">
                      <Loader2 size={28} className="animate-spin text-[var(--gold)]" />
                    </div>
                  ) : vouchers.length === 0 ? (
                    <p className="text-sm text-[var(--charcoal)]/60 py-10 text-center">
                      Aucun bon cadeau trouvé
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                      {vouchers.map((voucher) => (
                        <div key={voucher.id} className="border border-[var(--champagne)] p-3">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-[var(--charcoal)]/50">N° de bon</p>
                              <p className="font-mono text-[var(--gold)]">{voucher.voucher_number}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-[var(--charcoal)]/50">Client concerné</p>
                              <p>{voucher.client_name}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-[var(--charcoal)]/50">Date de fin validité</p>
                              <p>{formatDateFr(voucher.valid_until)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-[var(--charcoal)]/50 mb-1">Statut</p>
                              <select
                                value={voucher.status}
                                onChange={(e) => handleChangeVoucherStatus(voucher.id, e.target.value)}
                                className="w-full border border-[var(--champagne)] bg-white text-sm p-1.5"
                              >
                                <option value="en_cours">{voucherStatusLabels.en_cours}</option>
                                <option value="utilise">{voucherStatusLabels.utilise}</option>
                                <option value="depasse">{voucherStatusLabels.depasse}</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <p className="text-xs text-[var(--charcoal)]/55">
                              De: {voucher.sender_name} • Montant: {voucher.amount}€
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Télécharger PDF"
                                onClick={() => handleDownloadVoucher(voucher)}
                              >
                                <Download size={16} className="text-[var(--charcoal)]/70" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Supprimer"
                                onClick={() => handleDeleteVoucher(voucher)}
                              >
                                <Trash2 size={16} className="text-red-400" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Preset Amounts */}
            <TabsContent value="montants">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-['Cormorant_Garamond'] text-2xl">Montants prédéfinis</h2>
                <div className="flex gap-2">
                  {hasOrderChanges && (
                    <Button
                      onClick={savePresetsOrder}
                      disabled={isSaving}
                      className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
                    >
                      {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                      Enregistrer l'ordre
                    </Button>
                  )}
                  <Button
                    onClick={openCreateDialog}
                    className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
                  >
                    <Plus size={16} />
                    Nouveau montant
                  </Button>
                </div>
              </div>

              {isLoadingPresets ? (
                <div className="flex justify-center py-20">
                  <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
                </div>
              ) : presetAmounts.length === 0 ? (
                <div className="text-center py-16 bg-[var(--champagne)]/20 border border-[var(--champagne)]">
                  <Gift size={48} className="mx-auto mb-4 text-[var(--gold)]/50" />
                  <p className="text-[var(--charcoal)]/60">Aucun montant prédéfini</p>
                  <Button onClick={openCreateDialog} className="mt-4 bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none">
                    Créer le premier montant
                  </Button>
                </div>
              ) : (
                <Reorder.Group
                  axis="y"
                  values={presetAmounts}
                  onReorder={handleReorderPresets}
                  className="space-y-2"
                >
                  {presetAmounts.map((preset) => (
                    <Reorder.Item
                      key={preset.id}
                      value={preset}
                      className={`bg-white border border-[var(--champagne)] p-4 flex items-center justify-between cursor-grab active:cursor-grabbing ${!preset.is_active ? 'opacity-50' : ''}`}
                      whileDrag={{ scale: 1.02, boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}
                    >
                      <div className="flex items-center gap-4">
                        <GripVertical size={18} className="text-[var(--charcoal)]/30" />
                        <div>
                          <p className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">
                            {preset.amount}€
                          </p>
                          {preset.description && (
                            <p className="text-sm text-[var(--charcoal)]/60">{preset.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleTogglePreset(preset)}
                          title={preset.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {preset.is_active ? (
                            <ToggleRight size={18} className="text-green-500" />
                          ) : (
                            <ToggleLeft size={18} className="text-[var(--charcoal)]/30" />
                          )}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(preset)}>
                          <Edit2 size={16} className="text-[var(--charcoal)]/60" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePreset(preset)}>
                          <Trash2 size={16} className="text-red-400" />
                        </Button>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              )}
            </TabsContent>

            {/* Tab 3: Configuration */}
            <TabsContent value="config">
              <div className="bg-white border border-[var(--champagne)] p-8">
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-6">Configuration</h2>
                
                {isLoadingConfig ? (
                  <div className="flex justify-center py-10">
                    <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Allow Custom Amount */}
                    <div className="flex items-center justify-between p-4 bg-[var(--champagne)]/20">
                      <div>
                        <Label className="font-medium">Autoriser le montant personnalisé</Label>
                        <p className="text-sm text-[var(--charcoal)]/60">
                          Permettre aux clients de choisir un montant libre
                        </p>
                      </div>
                      <Switch
                        checked={config.allow_custom_amount}
                        onCheckedChange={(checked) => setConfig(prev => ({ ...prev, allow_custom_amount: checked }))}
                      />
                    </div>

                    {/* Min Amount */}
                    {config.allow_custom_amount && (
                      <>
                        <div>
                          <Label className="mb-2 block">Montant minimum (€)</Label>
                          <Input
                            type="number"
                            value={config.min_custom_amount}
                            onChange={(e) => setConfig(prev => ({ ...prev, min_custom_amount: parseFloat(e.target.value) || 10 }))}
                            min={1}
                            className="max-w-[200px]"
                          />
                        </div>

                        {/* Max Amount */}
                        <div>
                          <Label className="mb-2 block">Montant maximum (€)</Label>
                          <Input
                            type="number"
                            value={config.max_custom_amount}
                            onChange={(e) => setConfig(prev => ({ ...prev, max_custom_amount: parseFloat(e.target.value) || 500 }))}
                            min={1}
                            className="max-w-[200px]"
                          />
                        </div>
                      </>
                    )}

                    <Button
                      onClick={saveConfig}
                      disabled={isSaving}
                      className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
                    >
                      {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                      Enregistrer
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab 4: Prestations */}
            <TabsContent value="prestations">
              <div className="bg-white border border-[var(--champagne)] p-8">
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-2">Prestations éligibles</h2>
                <p className="text-sm text-[var(--charcoal)]/60 mb-6">
                  Sélectionnez les prestations disponibles en carte cadeau
                </p>
                
                {isLoadingPrestations ? (
                  <div className="flex justify-center py-10">
                    <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedPrestations).map(([category, items]) => (
                      <div key={category}>
                        <h3 className="font-medium text-[var(--charcoal)] mb-3 pb-2 border-b border-[var(--champagne)]">
                          {categoryLabels[category] || category}
                        </h3>
                        <div className="space-y-2">
                          {items.map((prestation) => (
                            <div
                              key={prestation.id}
                              className="flex items-center justify-between p-3 hover:bg-[var(--champagne)]/10 rounded"
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={prestation.gift_eligible}
                                  onCheckedChange={() => handleTogglePrestationEligible(prestation)}
                                  id={`prestation-${prestation.id}`}
                                />
                                <label 
                                  htmlFor={`prestation-${prestation.id}`}
                                  className="cursor-pointer"
                                >
                                  <span className="font-medium">{prestation.name}</span>
                                </label>
                              </div>
                              <span className="text-[var(--gold)] font-['Cormorant_Garamond'] text-lg">
                                {prestation.price}€
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {prestations.length === 0 && (
                      <p className="text-center text-[var(--charcoal)]/60 py-8">
                        Aucune prestation disponible
                      </p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Info Box */}
          <FadeIn delay={0.2}>
            <div className="mt-8 p-6 bg-[var(--champagne)]/30 border border-[var(--champagne)]">
              <h3 className="font-medium text-[var(--charcoal)] mb-2 flex items-center gap-2">
                <Gift size={16} className="text-[var(--gold)]" />
                À propos des cartes cadeaux
              </h3>
              <ul className="text-sm text-[var(--charcoal)]/70 space-y-1">
                <li>• Les clients peuvent acheter 3 types de cartes : montant prédéfini, montant personnalisé ou prestation</li>
                <li>• Les montants prédéfinis peuvent être réordonnés par glisser-déposer</li>
                <li>• Les prestations éligibles apparaissent dans l'onglet "Offrir une prestation"</li>
                <li>• Le montant personnalisé peut être désactivé globalement</li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Dialog for Create/Edit Preset */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              {editingPreset ? 'Modifier le montant' : 'Nouveau montant'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Montant (€) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="50"
                min={1}
              />
            </div>

            <div>
              <Label>Description (optionnelle)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Idéal pour une séance découverte"
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label>Actif</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="rounded-none"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSavePreset}
              disabled={isSaving}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {editingPreset ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
